/**
 * MobileBottomNav Component - T148
 *
 * Bottom navigation bar for mobile devices
 * Provides quick access to main sections
 */
import { Link, useMatchRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useSession } from '../../hooks/useTenantContext';

interface NavItem {
  label: string;
  path: string;
  icon: (active: boolean) => React.ReactNode;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    label: 'Rechnungen',
    path: '/invoices',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  // Sprint 006 A7: Ausgaben ist Kern-Workflow (Beleg unterwegs erfassen)
  // und gehört in die Bottom-Nav.
  {
    label: 'Ausgaben',
    path: '/expenses',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    ),
  },
  {
    label: 'Kunden',
    path: '/customers',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    label: 'Einstellungen',
    path: '/settings',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    label: 'Export',
    path: '/expenses/export',
    icon: (active) => (
      <svg
        className={`w-5 h-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    ),
  },
];

export function MobileBottomNav() {
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      document.documentElement.style.setProperty(
        '--mobile-nav-height',
        `${nav.getBoundingClientRect().height}px`
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--mobile-nav-height');
    };
  }, []);
  const matchRoute = useMatchRoute();
  const { data: sessionData } = useSession();
  const role = sessionData?.activeRole;
  const visibleItems = navItems.filter((item) => {
    if (role === 'steuerberater') return ['/expenses', '/expenses/export'].includes(item.path);
    if (role === 'admin') return item.path !== '/expenses' && item.path !== '/expenses/export';
    return true;
  });

  return (
    <nav
      ref={navRef}
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-t border-white/[0.08]"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
    >
      <div className="flex items-center justify-around max-w-lg mx-auto px-2 pt-2 pb-1">
        {visibleItems.map((item) => {
          const isActive =
            item.path === '/dashboard'
              ? matchRoute({ to: '/dashboard' }) !== false || matchRoute({ to: '/' }) !== false
              : matchRoute({ to: item.path, fuzzy: true }) !== false;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all !min-h-0 !min-w-0 ${
                isActive ? 'text-white' : 'text-white/50 active:text-white/70'
              }`}
            >
              {item.icon(isActive)}
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
